import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ChartOverlayContext = createContext();

import PropTypes from 'prop-types';
import { deleteLastOverlay as popLastOverlay, newOverlayGroupId } from '../utils/drawingStack';

export function ChartOverlayProvider(props) {
  const { children } = props;
  // Always store overlays as a flat array
  const [overlays, setOverlays] = useState([]);

  /**
   * The batch currently being filled, so one action's overlays share a groupId and undo removes
   * them together.
   *
   * Stamped HERE rather than in each study dialog, and that is the whole point. A study run adds
   * its overlays one `addOverlay` call at a time in a synchronous loop — `overlays.forEach(ov =>
   * addOverlay(ov))` — spread across a dozen dialogs. Requiring each of them to open and close a
   * batch would mean editing every one, and any dialog that forgot would produce a study whose
   * bands delete one at a time. Batching by arrival time needs no cooperation from callers and
   * cannot be forgotten by a new one.
   *
   * The window closes on a macrotask rather than a microtask deliberately: a study that awaits
   * between adds resolves those awaits as microtasks, so a microtask window would split one study
   * across several batches. Separate user actions are always separate macrotasks, so nothing
   * unrelated gets merged.
   */
  const batchRef = useRef(null);

  const currentBatchId = useCallback(() => {
    if (batchRef.current === null) {
      batchRef.current = newOverlayGroupId('study');
      setTimeout(() => { batchRef.current = null; }, 0);
    }
    return batchRef.current;
  }, []);

  // Every setter below updates through the functional form, so none of them depends on anything
  // outside setOverlays (which useState guarantees is stable). Wrapping them in useCallback with
  // an empty dependency list therefore changes no behaviour — it only stops each render handing
  // consumers brand-new function identities, which would otherwise force callers that legitimately
  // list them as hook dependencies to rebuild on every render.
  /**
   * Add one overlay or an array of them.
   *
   * <h2>AN INCOMING ID REPLACES THE ONE ALREADY THERE — this is a crash fix</h2>
   *
   * Running a fixed-id study twice used to append a SECOND overlay with the same id, the chart
   * processor emitted two series sharing it, and ECharts asserted:
   *
   *   id duplicates: pivot-markers-pivot-addition-8465-12-74
   *     at assert -> makeIdAndName -> GlobalModel._mergeOption -> initBase
   *
   * That exception escapes React and takes the whole application to a WHITE SCREEN. Clicking
   * "Pivot Addition Series" or "Fibonacci Pivot Pairs" a second time was enough to do it.
   *
   * <p>The fix is here rather than in those two handlers because they were not special. Every
   * study that draws under a fixed id has to remember `removeOverlay(ID)` first — several do,
   * which is exactly why they never crashed — and forgetting is SILENT until ECharts throws at
   * render time, in a stack that names neither the study nor the button. Making the container
   * refuse to hold two overlays under one id removes the whole class.
   *
   * <p>Overlays with NO id are untouched: drawing tools create many of those and they are
   * genuinely distinct figures. Only a defined, colliding id replaces.
   *
   * <p>Replace rather than ignore, because a re-run usually carries new content — the addition
   * series is anchored on the clicked bar, so running it again after clicking elsewhere is a new
   * answer to a new question, and keeping the old drawing would be the wrong one.
   */
  const addOverlay = useCallback((overlay) => {
    // Anything that already carries a groupId keeps it — a drawing tool has already decided which
    // figure its lines belong to, and that must win over arrival time.
    const batchId = currentBatchId();
    const stamp = (o) => (o && !o.groupId ? { ...o, groupId: batchId } : o);
    setOverlays((prev) => {
      // Always flatten: if overlay is an array, spread it; else push as single
      const incoming = (Array.isArray(overlay) ? overlay : [overlay]).map(stamp);

      const arrivingIds = new Set(
        incoming.filter(o => o && o.id !== undefined && o.id !== null).map(o => o.id));
      const kept = arrivingIds.size === 0
        ? prev
        : prev.filter(o => !(o && o.id !== undefined && o.id !== null && arrivingIds.has(o.id)));

      return [...kept, ...incoming];
    });
  }, [currentBatchId]);

  // Batch set overlays
  const setAllOverlays = useCallback((overlayArray) => {
    console.log('[OverlayContext] Setting all overlays (batch):', overlayArray);
    // Always flatten overlayArray
    const flat = overlayArray.flat ? overlayArray.flat() : overlayArray;
    setOverlays(flat);
  }, []);

  // Clear all overlays
  const clearOverlays = useCallback(() => {
    console.log('[OverlayContext] Clearing all overlays');
    setOverlays([]);
  }, []);

  // Remove the most recently added figure — a drawn one or a whole study run. See
  // utils/drawingStack for why the unit of undo is an action rather than a single overlay.
  const deleteLastOverlay = useCallback(() => {
    setOverlays((prev) => popLastOverlay(prev));
  }, []);

  // Remove a single overlay by id
  const removeOverlay = useCallback((id) => {
    console.log('[OverlayContext] Removing overlay with id:', id);
    setOverlays((prev) => {
      const updated = prev.filter((o) => o.id !== id);
      console.log('[OverlayContext] Overlays after remove:', updated);
      return updated;
    });
  }, []);

  // Update overlay position by id
  const updateOverlayPosition = useCallback((id, position) => {
    setOverlays((prev) => prev.map(o => o.id === id ? { ...o, position } : o));
  }, []);

  // Update arbitrary fields on an overlay by id
  const updateOverlay = useCallback((id, fields) => {
    setOverlays((prev) => prev.map(o => o.id === id ? { ...o, ...fields } : o));
  }, []);

  // Always provide overlays as a flat array
  const overlaysFlat = overlays.flat ? overlays.flat() : overlays;
  console.log('[OverlayContext] overlays provided to children:', overlaysFlat);
  return (
    <ChartOverlayContext.Provider value={{ overlays, addOverlay, setAllOverlays, clearOverlays, deleteLastOverlay, deleteLastDrawing: deleteLastOverlay, removeOverlay, updateOverlayPosition, updateOverlay }}>
      {children}
    </ChartOverlayContext.Provider>
  );
}

ChartOverlayProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useChartOverlays() {
  return useContext(ChartOverlayContext);
}
